import { useEffect, useRef, useState } from "react";
import { Link, Outlet } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { signOut } from "../lib/firebase";
import ToolsNav from "./ToolsNav";

export default function Layout() {
  const isAdmin = useAuth().isAdmin === true;
  return (
    <div className="min-h-screen flex flex-col">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 font-semibold tracking-tight">
            <img
              src="/v_transparent.png"
              alt=""
              className="h-7 w-7 object-contain"
            />
            <span>Document Review</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link
              to="/redline-creator"
              className="btn-secondary"
              title="Batch-compare Word documents into tracked-changes redlines"
            >
              ⇄ Redlines
            </Link>
            {isAdmin && (
              <Link
                to="/admin/conventions"
                className="btn-secondary"
                title="Admin: review attorney-flagged team conventions"
              >
                Conventions
              </Link>
            )}
            {isAdmin && (
              <Link
                to="/repository"
                className="btn-primary"
                title="Deal Repository — search all historical deals"
              >
                {/* Archive-box icon: this is the door to a separate area. */}
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-4 w-4"
                  aria-hidden="true"
                >
                  <path d="M2 4.25C2 3.56 2.56 3 3.25 3h13.5c.69 0 1.25.56 1.25 1.25v1.5C18 6.44 17.44 7 16.75 7H3.25C2.56 7 2 6.44 2 5.75v-1.5Z" />
                  <path
                    fillRule="evenodd"
                    d="M3 8.5h14v7.25c0 .69-.56 1.25-1.25 1.25H4.25C3.56 17 3 16.44 3 15.75V8.5Zm4.5 2.25a.75.75 0 0 1 .75-.75h3.5a.75.75 0 0 1 0 1.5h-3.5a.75.75 0 0 1-.75-.75Z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Repository</span>
                <svg
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className="h-3.5 w-3.5 -mr-0.5 opacity-70"
                  aria-hidden="true"
                >
                  <path
                    fillRule="evenodd"
                    d="M7.21 14.77a.75.75 0 0 1 .02-1.06L11.17 10 7.23 6.29a.75.75 0 1 1 1.04-1.08l4.5 4.25a.75.75 0 0 1 0 1.08l-4.5 4.25a.75.75 0 0 1-1.06-.02Z"
                    clipRule="evenodd"
                  />
                </svg>
              </Link>
            )}
            <ToolsNav />
            <UserMenu />
          </div>
        </div>
      </header>
      <main className="flex-1">
        <Outlet />
      </main>
    </div>
  );
}

function UserMenu() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const firstName = user.displayName?.split(" ")[0] ?? user.email ?? "Account";

  return (
    <div className="relative" ref={ref}>
      <button
        className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1 pl-1 pr-3 text-sm font-medium hover:bg-slate-50 transition"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <Avatar photoURL={user.photoURL} fallback={firstName} />
        <span>{firstName}</span>
      </button>
      {open && (
        <div
          role="menu"
          className="absolute right-0 top-full mt-2 w-64 rounded-lg border border-slate-200 bg-white shadow-lg overflow-hidden z-10"
        >
          <div className="px-4 py-3 border-b border-slate-100">
            <div className="font-medium text-sm">{user.displayName ?? firstName}</div>
            <div className="text-xs text-slate-500 mt-0.5 truncate" title={user.email ?? ""}>
              {user.email}
            </div>
          </div>
          <button
            role="menuitem"
            className="w-full text-left px-4 py-2.5 text-sm hover:bg-slate-50"
            onClick={() => {
              setOpen(false);
              signOut();
            }}
          >
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}

function Avatar({ photoURL, fallback }: { photoURL: string | null; fallback: string }) {
  const [errored, setErrored] = useState(false);
  if (photoURL && !errored) {
    return (
      <img
        src={photoURL}
        alt=""
        className="h-7 w-7 rounded-full object-cover"
        // Google CDN sometimes 403s without this when a referrer is sent.
        referrerPolicy="no-referrer"
        onError={() => setErrored(true)}
      />
    );
  }
  return (
    <div className="h-7 w-7 rounded-full bg-slate-200 flex items-center justify-center text-xs font-semibold text-slate-700">
      {fallback.charAt(0).toUpperCase()}
    </div>
  );
}
